import { createContext, useContext, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { getCurrentUser, logoutUser } from "@/api/user";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const {
    data: user = null,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["current-user"],
    queryFn: async () => {
      try {
        return await getCurrentUser();
      } catch (error) {
        const status = error?.response?.status;

        // No active session is a valid state, not an app error.
        if (status === 401 || status === 403) {
          return null;
        }

        // --- FIX: Catch Network Errors (ERR_CONNECTION_REFUSED) ---
        // If the error is a network error (e.g. localhost offline on mobile),
        // just return null instead of throwing. This prevents the 2.3s mobile freeze.
        if (error?.message === "Network Error" || error?.code === "ERR_NETWORK") {
          console.warn("Auth API offline. Treating user as logged out.");
          return null; 
        }

        // If it's a genuine server 500 error, throw it so TanStack Query knows it failed.
        throw error;
      }
    },
    retry: false, // Don't retry. One fail means fail.
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const isAuthenticated = !!user;

  const logout = useCallback(async () => {
    try {
      await logoutUser();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      // Clear all cached data including user session
      queryClient.clear();
      // Navigate directly to login page without transition (replace: true prevents back button issues)
      navigate("/admin", { replace: true });
    }
  }, [queryClient, navigate]);

  const invalidateUser = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["current-user"] });
  }, [queryClient]);

  const refetchUser = useCallback(async () => {
    return queryClient.getQueryData(["current-user"]) || null;
  }, [queryClient]);

  const value = {
    user,
    isAuthenticated,
    isLoading,
    isError,
    logout,
    refetchUser,
    invalidateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};